import { useEffect, useState } from 'react';
import { Check, Clock3, FileImage, ListChecks, PanelLeftClose, Plus, Trash2, X } from 'lucide-react';
import type { PatternProject } from '../types';
import { ProjectThumbnail } from './ProjectThumbnail';

function relativeTime(value: number) {
  const minutes = Math.floor((Date.now() - value) / 60000);
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  return new Date(value).toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' });
}

export function ProjectPanel({ projects, activeId, onOpen, onNew, onDelete, onDeleteMany, onCollapse }: {
  projects: PatternProject[];
  activeId?: string;
  onOpen: (project: PatternProject) => void;
  onNew: () => void;
  onDelete: (project: PatternProject) => void;
  onDeleteMany: (projects: PatternProject[]) => Promise<boolean>;
  onCollapse?: () => void;
}) {
  const [managing, setManaging] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  useEffect(() => {
    const available = new Set(projects.map((project) => project.id));
    setSelectedIds((current) => current.filter((id) => available.has(id)));
    if (!projects.length) setManaging(false);
  }, [projects]);
  const selected = new Set(selectedIds);
  const selectedProjects = projects.filter((project) => selected.has(project.id));
  const toggle = (project: PatternProject) => {
    setSelectedIds((current) => current.includes(project.id) ? current.filter((id) => id !== project.id) : [...current, project.id]);
  };
  const exitManage = () => {
    setManaging(false);
    setSelectedIds([]);
  };

  return (
    <aside className="project-panel">
      <div className="project-head"><div><p className="eyebrow">本地项目</p><h2>我的图纸</h2></div><div className="project-head-actions">{projects.length > 0 && <button className={`icon-button ${managing ? 'manage-active' : ''}`} type="button" onClick={() => managing ? exitManage() : setManaging(true)} aria-label={managing ? '退出批量管理' : '批量管理图纸'} title={managing ? '退出批量管理' : '批量管理'}>{managing ? <X size={18} /> : <ListChecks size={18} />}</button>}{onCollapse && <button className="icon-button" type="button" onClick={onCollapse} aria-label="收起项目栏"><PanelLeftClose size={18} /></button>}<button className="icon-button dark" type="button" onClick={onNew} aria-label="新建图纸"><Plus size={19} /></button></div></div>
      <p className="project-note">{managing ? `已选择 ${selectedIds.length}/${projects.length} 张图纸，删除会同步移除云端副本。` : '图纸存在这台设备。登录后，不同账号的图纸会分开显示。'}</p>
      <div className="project-list">
        {projects.length === 0 && <div className="project-empty"><FileImage size={25} /><strong>这里还是空的</strong><span>生成或导入一张图纸后，就会出现在这里。</span></div>}
        {projects.map((project) => (
          <div className={`project-item ${project.id === activeId ? 'active' : ''}`} key={project.id}>
            <button className={`project-open ${managing ? 'project-manage-open' : ''}`} type="button" aria-pressed={managing ? selected.has(project.id) : undefined} onClick={() => managing ? toggle(project) : onOpen(project)}>
              {managing && <span className={`project-check ${selected.has(project.id) ? 'checked' : ''}`} aria-hidden="true">{selected.has(project.id) && <Check size={14} />}</span>}
              <ProjectThumbnail project={project} />
              <span><strong>{project.name}</strong><small><Clock3 size={13} />{relativeTime(project.updatedAt)} · {project.width}×{project.height}</small></span>
            </button>
            {!managing && <button className="project-delete" type="button" onClick={() => onDelete(project)} aria-label={`删除 ${project.name}`}><Trash2 size={15} /></button>}
          </div>
        ))}
      </div>
      {managing ? <div className="project-bulk-actions">
        <button className="secondary-button" type="button" onClick={() => setSelectedIds(selectedIds.length === projects.length ? [] : projects.map((project) => project.id))}>{selectedIds.length === projects.length ? '取消全选' : '全选'}</button>
        <button className="primary-button danger" type="button" disabled={!selectedProjects.length} onClick={() => { void onDeleteMany(selectedProjects).then((deleted) => { if (deleted) exitManage(); }).catch(() => undefined); }}><Trash2 size={16} />删除所选</button>
      </div> : <button className="secondary-button new-project" type="button" onClick={onNew}><Plus size={18} />新建图纸</button>}
    </aside>
  );
}
